'use client';

import { useState } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@/hooks/useTranslations';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateInvoiceSchema } from '@/schemas';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormError } from '@/components/form';
import { SelectClient } from '@/features/invoices/select-client';
import { CreateClientPopup } from '@/features/clients/create/create-client-popup';
import { createInvoice } from '@/actions';
import { calculateInvoiceTotals } from '@/lib/helpers/invoice-math';
import { getCurrencyFormatter } from '@/lib/currency-formatter';
import InvoiceNumberGenerator from '../invoice-number';
import { Items } from '../items';
import { InvoiceType } from '../invoice-type';
import { useInvoiceContext } from '@/contexts/invoice-context';
import { useClientContext } from '@/contexts/client-context';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { LoadingButton } from '@/components/loading-button';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { SingleDatePicker } from '../single-date-picker';
import { RecurringFrequencySelect } from '../recurring-frequency-select';
import { addDays, addMonths } from 'date-fns';
import { DatePickerWithRange } from '../date-range';
import { advanceRecurringDate } from '@/lib/helpers/recurring-helpers';
import { toast } from 'sonner';

export const CreateInvoiceForm = () => {
  const [error, setError] = useState<string | undefined>('');
  const { status, wrap } = useSubmitStatus();

  const t = useTranslations('CreateInvoicePopup');
  const common = useTranslations('Common');

  const [items, setItems] = useState([
    {
      description: '',
      qty: 0,
      unit: '',
      price: 0,
      tax: 0,
      total: 0,
    },
  ]);

  const form = useForm<z.infer<typeof CreateInvoiceSchema>>({
    resolver: zodResolver(CreateInvoiceSchema),
    defaultValues: {
      clientId: '',
      invoiceNumber: '',
      invoiceType: 'Invoice',
      status: 'UNPAID',
      ddvAdditionalInput: '',
      isRecurring: false,
      recurringFrequency: 'MONTHLY',
      recurringNextDate: addMonths(new Date(), 1),
      recurringEndDate: undefined,

      serviceDate: {
        from: undefined,
        to: undefined,
      },
      issuedDate: new Date(),
      dueDate: addDays(new Date(), 15),
    },
  });

  const isRecurring = form.watch('isRecurring');

  const { dispatch } = useInvoiceContext();
  const { state: client } = useClientContext();

  const onSubmit = (values: z.infer<typeof CreateInvoiceSchema>) => {
    setError('');

    wrap(
      () =>
        new Promise<void>((resolve, reject) => {
          // Extract year from service date on client side to avoid timezone issues
          // This ensures we use the calendar date the user selected, not the UTC representation
          const serviceYear = values.serviceDate.from
            ? values.serviceDate.from.getFullYear()
            : new Date().getFullYear();

          const getNextRecurringDate = () => {
            const from = values.serviceDate.from || new Date();
            return advanceRecurringDate(
              from,
              values.recurringFrequency ?? 'MONTHLY'
            );
          };

          createInvoice({
            clientId: values.clientId,
            invoiceNumber: values.invoiceNumber,
            invoiceType: values.invoiceType,
            status: 'UNPAID',
            serviceDate: values.serviceDate,
            serviceYear,
            dueDate: values.dueDate,
            issuedDate: values.issuedDate || new Date(),
            items,
            ddvAdditionalInput: values.ddvAdditionalInput,
            isRecurring: values.isRecurring,
            recurringFrequency: values.isRecurring
              ? values.recurringFrequency
              : undefined,
            recurringNextDate: values.isRecurring
              ? (values.recurringNextDate ?? getNextRecurringDate())
              : undefined,
            recurringEndDate: values.isRecurring
              ? (values.recurringEndDate ?? null)
              : null,
          })
            .then((data) => {
              if (data.success) {
                toast.success(data.success);
                resolve();

                setTimeout(() => {
                  dispatch({ type: 'CLOSE_CREATE_INVOICE_POPUP' });
                  form.reset();
                }, 1500);
              } else {
                const errorMessage = data.error || 'Something went wrong!';
                toast.error(errorMessage);
                setError(errorMessage);
                reject();
              }
            })
            .catch(() => {
              const errorMessage = 'Something went wrong!';
              toast.error(errorMessage);
              setError(errorMessage);
              reject();
            });
        })
    );
  };

  const {
    subtotal: subTotalItemsSum,
    tax: subTotalTaxSum,
    total: totalSum,
  } = calculateInvoiceTotals(items);

  const session = useSession();

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <div className="flex justify-between gap-2 flex-col sm:flex-row">
              <FormField
                control={form.control}
                name="invoiceType"
                render={({ field }) => (
                  <FormItem className="flex flex-col w-full sm:w-1/2">
                    <FormLabel>{t('type')}</FormLabel>

                    <FormControl>
                      <InvoiceType
                        placeholder={t('selectType')}
                        value={field.value}
                        onSelect={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <InvoiceNumberGenerator />
            </div>

            <div className="border-b"></div>

            <FormField
              control={form.control}
              name="serviceDate"
              render={({ field }) => (
                <FormItem className="flex flex-col w-full ">
                  <FormLabel>{t('dateOfServicePerformed')}</FormLabel>

                  <FormControl>
                    <DatePickerWithRange
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-between gap-2 flex-col sm:flex-row">
              <FormField
                control={form.control}
                name="dueDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col w-full sm:w-1/2">
                    <FormLabel>{t('dueDate')}</FormLabel>

                    <FormControl>
                      <SingleDatePicker
                        id="due-date"
                        ariaLabel="Due date"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="issuedDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col w-full sm:w-1/2">
                    <FormLabel htmlFor="issued-date">
                      {t('issuedDate')}
                    </FormLabel>

                    <FormControl>
                      <SingleDatePicker
                        id="issued-date"
                        ariaLabel="Issued date"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="border-b"></div>

            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('selectClient')}</FormLabel>
                  <SelectClient
                    placeholder={t('selectClient')}
                    value={field.value}
                    onSelect={(value) => {
                      field.onChange(value);
                    }}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="border-b"></div>

            <Items
              status={status}
              items={items}
              setItems={setItems}
              currency={session.data?.user.currency}
            />

            <div className="border-b"></div>

            <FormField
              control={form.control}
              name="ddvAdditionalInput"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('ddvAdditionalInputLabel')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={status === 'loading'}
                      placeholder={t('ddvAdditionalInputPlaceholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="border-b"></div>

            {/* Recurring */}
            <div className="flex flex-col gap-4 p-3 border rounded-lg bg-muted/30">
              <FormField
                control={form.control}
                name="isRecurring"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between">
                    <FormLabel>{t('recurring')}</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={status === 'loading'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              {isRecurring && (
                <>
                  <FormField
                    control={form.control}
                    name="recurringFrequency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('recurringFrequency')}</FormLabel>
                        <FormControl>
                          <RecurringFrequencySelect
                            value={field.value ?? 'MONTHLY'}
                            onChange={field.onChange}
                            disabled={status === 'loading'}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="recurringNextDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('recurringNextDate')}</FormLabel>
                        <FormControl>
                          <SingleDatePicker
                            id="recurring-next-date"
                            ariaLabel="Next recurring date"
                            value={field.value ?? null}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="recurringEndDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('recurringEndDate')}</FormLabel>
                        <FormControl>
                          <SingleDatePicker
                            id="recurring-end-date"
                            ariaLabel="Recurring end date (optional)"
                            value={field.value ?? null}
                            onChange={(d) => field.onChange(d ?? null)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </div>

            <div className="border-b"></div>

            <div className="p-3">
              <div className="flex justify-between">
                <h6>{t('subTotal')}</h6>

                <span>
                  {getCurrencyFormatter(
                    subTotalItemsSum,
                    session.data?.user.currency
                  )}
                </span>
              </div>

              <div className="flex justify-between">
                <h6>{t('tax')}:</h6>

                <span>
                  {getCurrencyFormatter(
                    subTotalTaxSum,
                    session.data?.user.currency
                  )}
                </span>
              </div>

              <div className="flex justify-between">
                <h6>{t('total')}:</h6>

                <span>
                  {getCurrencyFormatter(totalSum, session.data?.user.currency)}
                </span>
              </div>
            </div>
          </div>

          {error && /limit|plan|upgrade/i.test(error) ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10 p-4">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">
                {error}
              </p>
              <Link
                href="/pricing"
                className="inline-block text-sm font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
              >
                See plans →
              </Link>
            </div>
          ) : (
            <FormError message={error} />
          )}

          <LoadingButton
            isFull={true}
            type="submit"
            status={status}
            idleText={common('confirm')}
          />
        </form>
      </Form>
      {client.isCreateClientPopupOpen && <CreateClientPopup />}
    </>
  );
};
